import * as React from 'react'
import { DocumentsController } from '../../controllers'
import { saveAs } from 'file-saver'
import Notification from '../../utilities/Notification'
import PowerSelect from '../Form/PowerSelect'
import ModalHeader from './Parts/ModalHeader'
import ModalLoader from './Parts/ModalLoader'
import ModalMiddle from './Parts/ModalMiddle'
import ModalWindow from './Parts/ModalWindow'
import ModalContent from './Parts/ModalContent'
import TooltipError from '../Tooltips/ErrorTooltip'
import Button from '../Button/Button'
import { connect } from 'react-redux'
import { WithTranslation, withTranslation } from 'react-i18next'
import { AnyAction, Dispatch } from 'redux'
import { closeExportDocumentsModal } from '../../store/modals/actions'
import ReactSelectTheme from '../Form/ReactSelectTheme'
import { BlobResponse, CurrentUser, ExportTimePeriod } from '../../types'
import ExportHelper from '../../helpers/ExportHelper'
import moment from '../../utilities/Moment'
import DocumentHelper from '../../helpers/DocumentHelper'
import DateInput from '../Form/DateInput'
import { AppState } from '../../store'

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  close: typeof closeExportDocumentsModal
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation

interface IState {
  didInitialLoad: boolean
  exportPeriodType: ExportTimePeriod
  start: string
  end: string
  errors: any
  isSubmitting: boolean
}

class ExportDocumentsModal extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    const { start, end } = ExportHelper.getPeriodRange(ExportTimePeriod.ALL)

    this.state = {
      didInitialLoad: false,
      exportPeriodType: ExportTimePeriod.ALL,
      start: start,
      end: end,
      errors: {},
      isSubmitting: false,
    }

    this.onExportPeriodTypeSelect = this.onExportPeriodTypeSelect.bind(this)
    this.onStartDateChange = this.onStartDateChange.bind(this)
    this.onEndDateChange = this.onEndDateChange.bind(this)
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onDocumentExportModalClose = this.onDocumentExportModalClose.bind(this)
    this.onErrorsDismiss = this.onErrorsDismiss.bind(this)
  }

  componentDidMount() {
    requestAnimationFrame(() => { this.setState({ didInitialLoad: true }) })
  }

  onExportPeriodTypeSelect(option) {
    const exportPeriodType = option.value

    const { start, end } = ExportHelper.getPeriodRange(exportPeriodType)

    this.setState({
      exportPeriodType: option.value,
      start: start,
      end: end
    })
  }

  onStartDateChange(value) {
    const newStartDate = moment(value)

    this.setState({
      start: newStartDate.isValid() ? newStartDate.format('YYYY-MM-DD') : null
    })
  }

  onEndDateChange(value) {
    const newEndDate = moment(value)

    this.setState({
      end: newEndDate.isValid() ? newEndDate.format('YYYY-MM-DD') : null
    })
  }


  async onFormSubmit(e) {
    e.preventDefault();

    const { close, t } = this.props
    const { start, end } = this.state

    this.setState({ isSubmitting: true })
    try {
      const response = await DocumentsController.exportDocuments({
        start: start,
        end: end
      })

      // @ts-ignore
      if (response.success) {
        Notification.notifySuccess(t('ExportDocumentsModal::You\'ll receive the archive with documents for the selected period via email.'))
      }

      close()
    } catch (ex) {
      console.error(ex)
    } finally {
      this.setState({ isSubmitting: false })
    }
  }

  onDocumentExportModalClose() {
    this.props.close()
  }

  onErrorsDismiss() {
    this.setState({
      errors: {}
    })
  }

  render() {
    const { t, currentUser: { workspace: { setting } } } = this.props
    const {
      didInitialLoad,
      errors,
      exportPeriodType,
      start,
      end,
      isSubmitting,
    } = this.state

    const exportPeriodOptions = ExportHelper.getPeriodOptions()
    const selectedExportPeriodType = exportPeriodOptions.find(option => option.value === exportPeriodType)

    return (
      <ModalWindow>
        <ModalHeader
          title={t('ExportDocumentsModal::Export Documents')}
          onCloseClick={this.onDocumentExportModalClose}
        />

        {!didInitialLoad && <ModalLoader />}
        {didInitialLoad && <ModalMiddle>
          <ModalContent>
            <form onSubmit={this.onFormSubmit}>
              <div className='grid'>
                <div className='grid-cell with-12col'>
                  <div className='form-item'>
                    <label>
                      {t('ExportDocumentsModal::Period')}
                    </label>

                    <PowerSelect
                      options={exportPeriodOptions}
                      value={selectedExportPeriodType}
                      onChange={this.onExportPeriodTypeSelect}
                      noOptionsMessage={() => t('ExportDocumentsModal::No option found')}
                      isClearable={false}
                      theme={ReactSelectTheme}
                    />
                  </div>
                </div>

                {exportPeriodType === ExportTimePeriod.CUSTOM && <>
                  <div className='grid-cell with-6col'>
                    <div className='form-item'>
                      <label>
                        {t('ExportDocumentsModal::Start date')}
                      </label>

                      <DateInput
                        name='start_date'
                        dateFormat={setting.date_format}
                        timeFormat={false}
                        value={moment(start)}
                        inputProps={{ placeholder: setting.date_format }}
                        onChange={this.onStartDateChange}
                        closeOnSelect
                      />
                    </div>
                  </div>

                  <div className='grid-cell with-6col'>
                    <div className='form-item'>
                      <label>
                        {t('ExportDocumentsModal::End date')}
                      </label>
                      <DateInput
                        name='end_date'
                        dateFormat={setting.date_format}
                        timeFormat={false}
                        value={moment(end)}
                        inputProps={{ placeholder: setting.date_format }}
                        onChange={this.onEndDateChange}
                        closeOnSelect
                      />
                    </div>
                  </div>
                </>}
              </div>

              <input type='submit' style={{ display: 'none' }} />
            </form>
          </ModalContent>

          <div className='modal-footer'>
            <div />
            <div className='modal-footer-actions'>
              <div key='main-action' className='popover-wrapper'>
                <TooltipError
                  errors={errors}
                  onDismiss={this.onErrorsDismiss}
                />
                <Button
                  type='success'
                  text={t('ExportDocumentsModal::Export')}
                  onClick={this.onFormSubmit}
                  isLoading={isSubmitting}
                />
              </div>
            </div>
          </div>
        </ModalMiddle>}
      </ModalWindow>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    },
  } = state

  return {
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeExportDocumentsModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ExportDocumentsModal))